import React, { useMemo } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import ModifierOption from './ModifierOption';

/**
 * ModifierGroup - Container for modifier options with validation
 * 
 * Features:
 * - Required vs optional indicator
 * - Single-select (radio) vs multi-select (checkbox) logic
 * - Min/max selection enforcement
 * - Clean validation messaging
 * - Selection count display
 */
export default function ModifierGroup({
  group,
  selectedOptions = [],
  onSelectionChange,
  className = '',
}) {
  const {
    id,
    name,
    description,
    required = false,
    min_selections = 0,
    max_selections = 999,
    options = [],
  } = group;

  // Determine selection type based on max_selections
  const selectionType = max_selections === 1 ? 'single' : 'multi';
  
  // Effective min (required groups need at least 1 if not specified)
  const effectiveMin = required && min_selections === 0 ? 1 : min_selections;

  // Count available options
  const availableOptions = useMemo(() => 
    options.filter(opt => opt.is_available !== false),
    [options]
  );

  // Validation state
  const selectedCount = selectedOptions.length;
  const isComplete = selectedCount >= effectiveMin;
  const canSelectMore = selectedCount < max_selections;
  const showValidation = required && !isComplete;

  // Check if group can be satisfied (has enough available options)
  const canBeSatisfied = availableOptions.length >= effectiveMin;

  // Handle option selection
  const handleOptionSelect = (option, isSelected) => {
    let newSelection;

    if (selectionType === 'single') {
      // Single select: replace selection
      newSelection = isSelected ? [option] : [];
    } else {
      // Multi select: toggle
      if (isSelected) {
        if (canSelectMore) {
          newSelection = [...selectedOptions, option];
        } else {
          // At max, don't add
          return;
        }
      } else {
        newSelection = selectedOptions.filter(opt => opt.id !== option.id);
      }
    }

    onSelectionChange(group.id, newSelection);
  };

  // Build selection rules text
  const getRulesText = () => {
    if (selectionType === 'single') {
      return required ? 'Select one' : 'Select one (optional)';
    }
    
    if (effectiveMin === max_selections) {
      return `Select ${effectiveMin}`;
    }
    
    if (effectiveMin > 0 && max_selections < 999) {
      return `Select ${effectiveMin}-${max_selections}`;
    }
    
    if (effectiveMin > 0) {
      return `Select at least ${effectiveMin}`;
    }
    
    if (max_selections < 999) {
      return `Select up to ${max_selections}`;
    }
    
    return 'Optional';
  };

  return (
    <div className={`${className}`}>
      {/* Group Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-white">
              {name}
            </h3>
            {required && (
              <span className={`
                text-xs font-semibold px-2 py-0.5 rounded-full
                ${isComplete 
                  ? 'bg-[#10b981]/20 text-[#10b981]' 
                  : 'bg-[#ef4444]/20 text-[#ef4444]'
                }
              `}>
                Required
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-gray-400 mt-1">{description}</p>
          )}
        </div>

        {/* Selection Rules & Count */}
        <div className="text-right flex-shrink-0">
          <span className="text-sm text-gray-400">
            {getRulesText()}
          </span>
          {selectionType === 'multi' && selectedCount > 0 && (
            <p className="text-sm text-[#f5a623] mt-0.5">
              {selectedCount} selected
            </p>
          )}
        </div>
      </div>

      {/* Cannot be satisfied warning */}
      {!canBeSatisfied && required && (
        <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-[#ef4444]/10 border border-[#ef4444]/20">
          <AlertCircle size={18} className="text-[#ef4444] flex-shrink-0" />
          <p className="text-sm text-[#ef4444]">
            Not enough options available. Please choose a different item.
          </p>
        </div>
      )}

      {/* Validation Message */}
      {showValidation && canBeSatisfied && (
        <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-[#f5a623]/10 border border-[#f5a623]/20">
          <AlertCircle size={18} className="text-[#f5a623] flex-shrink-0" />
          <p className="text-sm text-[#f5a623]">
            Please make a selection to continue
          </p>
        </div>
      )}

      {/* Options List */}
      <div className="space-y-2">
        {options.map((option) => {
          const isSelected = selectedOptions.some(opt => opt.id === option.id);
          const isUnavailable = option.is_available === false;
          
          // Disable if at max and not selected (for multi-select)
          const isDisabled = selectionType === 'multi' && !canSelectMore && !isSelected;

          return (
            <ModifierOption
              key={option.id}
              option={option}
              selected={isSelected}
              disabled={isDisabled}
              unavailable={isUnavailable}
              selectionType={selectionType}
              onSelect={handleOptionSelect}
            />
          );
        })}
      </div>

      {/* Completion indicator for required groups */}
      {required && isComplete && (
        <div className="flex items-center gap-2 mt-3 text-[#10b981]">
          <CheckCircle size={16} />
          <span className="text-sm font-medium">Selection complete</span>
        </div>
      )}
    </div>
  );
}
